/* eslint-disable indent */
import React from 'react';
import { useMeasurement, useSirenEntity } from '~/utility/BinSentry-ui-utility';
import { makeStyles, Table, TableBody, TableCell, TableRow, withStyles, useMediaQuery, useTheme, Icon } from '~/lib/bn-material-ui';
import classNames from 'classnames';
import { OrderState } from '../../../components/order-state';
import { useOrdersDialogStateSelector, useOrdersDialogDispatch } from '../context/orders-dialog-context';
import { windowConfirmUnsavedChanges } from '../context/orders-dialog-context-utilities';
import { OrderFeedType } from '~/orders/components/order-feed-type';
import { getAllSiteBinEntities } from '~/orders/dialogs/order-planner-dialog/context/orders-dialog-context-get-actions';
import BnCopyToClipboard from '~/common/bn-copy-to-clipboard';

export const firstLastTableElementPadding = {
  '&:first-child': {
    paddingLeft: '1.125rem',
  },
  '&:last-child': {
    paddingRight: '1.125rem',
  },
};

const useOrderRowStyles = makeStyles(({ palette, breakpoints }) => ({
  labelCell: {
    verticalAlign: 'baseline',
  },
  feedNameCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexGrow: 0,
  },
  orderStateCell: {
    display: 'flex',
    alignItems: 'center',
    '& .material-icons': {
      fontSize: '1.25em',
      overflow: 'visible',
    },
  },
  medicatedIcon: {
    color: palette.gray[60],
    marginLeft: '.5rem',
    display: 'inline-flex',
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: palette.grey[200],
      [breakpoints.down('sm')]: {
        backgroundColor: 'unset',
      },
    },
    '&:hover': {
      backgroundColor: palette.grey[400],
      cursor: 'pointer',
      [breakpoints.down('sm')]: {
        backgroundColor: 'unset',
        cursor: 'unset',
      },
    },
    [breakpoints.up('md')]: {
      '& > td > $copyOrderNumber svg': {
        visibility: 'hidden',
      },
      '&:hover > td > $copyOrderNumber svg': {
        visibility: 'visible',
      },
    },
    '&.selected': {
      backgroundColor: palette.blue[10],
      // This doesn't use border, as table items do not respect box-sizing: border-box
      boxShadow: `2px 2px inset ${palette.blue[30]}, -2px -2px inset ${palette.blue[30]}`,
    },
    '&.selected:first-child': {
      // The table header overlaps the first table-item by 1px, this fixes the top psuedo-border to appear consistent
      boxShadow: `2px 3px inset ${palette.blue[30]}, -2px -2px inset ${palette.blue[30]}`,
    },
    [breakpoints.down('sm')]: {
      borderTop: `1px solid ${palette.gray[50]}`,
      marginLeft: '1.125rem',
      marginRight: '1.125rem',
    },
  },
  mobileHeaderRow: {
    borderTop: `1px solid ${palette.gray[50]}`,
    ['& .MuiTableCell-body']: {
      padding: '0.5rem 0',
      border: 'none',
      paddingLeft: '1.125rem',
    },
  },
  headerTableCell: {
    fontWeight: '700',
    width: '9.5rem',
  },
  dataTableCell: {
    paddingLeft: '2rem',
  },
  expandTableCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& .fas': {
      color: palette.blue[40],
    },
  },
  expandedTable: {
    marginLeft: '1.125rem',
    marginRight: '1.125rem',
    width: 'calc(100% - 2.25rem)',
    ['& .MuiTableCell-body']: {
      padding: '0.5rem 0',
      border: 'none',
    },
  },
  expandedTableCell: {
    padding: '0',
    background: palette.blue[10],
  },
  copyOrderNumber: {},
}));

const StyledTableCell = withStyles(() => ({
  root: {
    verticalAlign: 'baseline',
    border: 'none',
    padding: '0.3em 0 0.125em 0.5rem',
    ...firstLastTableElementPadding,
    lineHeight: '2.125em',
  },
}))(TableCell);

export function SiteFeedOrderRow ({ order, columnConfig }) {
  const [selectedOrderId, barns, hasUnsavedChanges] = useOrdersDialogStateSelector([
    'selectedOrderId', 'barns', 'hasUnsavedChanges',
  ]);
  const allSitesBins = getAllSiteBinEntities(barns);
  const allSitesBarns = Object.values(barns).map(({ barnEntity }) => barnEntity);

  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

  const dispatch = useOrdersDialogDispatch();
  const isSelected = order.getLinkByRel('self').href === selectedOrderId;
  const barnId = order.getLinkByRel('https://api.binsentry.com/rel/barn').href;
  const ordersBarn = allSitesBarns.find(barn => barn.getLinkByRel('self').href === barnId);

  const orderBinAllocations = order.getSubEntitiesByRel('https://api.binsentry.com/rel/order-bin-allocation');
  const orderBinIds = orderBinAllocations.map(item => item.getLinkByRel('https://api.binsentry.com/rel/bin').href);

  const binNames = orderBinIds.map((binId) => {
    const foundBin = allSitesBins.find((bin) => bin.getLinkByRel('self').href === binId);
    return foundBin?.properties.name || 'unknown';
  });

  const _onClick = () => {
    if (hasUnsavedChanges && selectedOrderId && !windowConfirmUnsavedChanges()) {
      return;
    }
    dispatch({ type: 'select-order', selectedOrderEntity: order });
  };

  return isMobileView
    ? <MobileSiteFeedRow
      order={order}
      binNames={binNames}
      barnName={ordersBarn?.properties.name}
      onClick={_onClick}
      isSelected={isSelected}
      columnConfig={columnConfig}
    />
    : <DesktopSiteFeedRow
      order={order}
      binNames={binNames}
      barnName={ordersBarn?.properties.name}
      onClick={_onClick}
      isSelected={isSelected}
      columnConfig={columnConfig}
    />;
}

function DesktopSiteFeedRow ({ order, binNames, barnName, onClick, isSelected, columnConfig }) {
  const styles = useOrderRowStyles();
  const { entity: feedEntity } = useSirenEntity({ href: order.getSubEntityByRel('https://api.binsentry.com/rel/feed')?.href });
  const { formulaId, bulkDensity } = feedEntity?.properties || {};
  const orderMeasurement = useMeasurement({ name: 'orderAmount' });

  const convertedOrderAmount = orderMeasurement.convert({
    volume: order.properties.quantity,
    bulkDensity,
  });

  const orderNumber = order.properties.orderNumber;
  return (
    <TableRow
      className={classNames(styles.tableRow, isSelected && 'selected')}
      onClick={onClick}
    >
      {columnConfig.showOrderNumberColumn && <StyledTableCell>
        <BnCopyToClipboard className={styles.copyOrderNumber} showIcon={!!orderNumber}>
          {orderNumber}
        </BnCopyToClipboard>
      </StyledTableCell>}
      <StyledTableCell>
        <div className={styles.feedNameCell}>
          <OrderFeedType orderEntity={order} iconClassName={styles.medicatedIcon} />
        </div>
      </StyledTableCell>
      <StyledTableCell>{formulaId}</StyledTableCell>
      <StyledTableCell>{barnName}</StyledTableCell>
      <StyledTableCell>{binNames.join(', ')}</StyledTableCell>
      <StyledTableCell className={styles.orderStateCell}>
        <OrderState orderEntity={order} textOnly />
      </StyledTableCell>
      <StyledTableCell>
        {feedEntity ? `${convertedOrderAmount} ${orderMeasurement.unit}` : ' - '}
      </StyledTableCell>
    </TableRow>
  );
}

function MobileSiteFeedRow ({ order, isSelected, onClick, barnName, binNames, columnConfig }) {
  const styles = useOrderRowStyles();
  const { entity: feedEntity } = useSirenEntity({ href: order.getSubEntityByRel('https://api.binsentry.com/rel/feed')?.href });
  const { bulkDensity } = feedEntity?.properties || {};
  const orderMeasurement = useMeasurement({ name: 'orderAmount' });
  const orderBinAllocations = order.getSubEntitiesByRel('https://api.binsentry.com/rel/order-bin-allocation');
  const theme = useTheme();

  const orderAllocationVolumes = orderBinAllocations.map(item =>
    orderMeasurement.convert({
      volume: item.properties.volume,
      bulkDensity,
    }),
  );

  return (
    <>
      <TableRow
        className={classNames(styles.tableRow, styles.mobileHeaderRow)}
        onClick={onClick}
        style={{ ...isSelected && { backgroundColor: theme.palette.blue[10] } }}
      >
        <TableCell className={styles.headerTableCell}>Barn Name</TableCell>
        <TableCell className={styles.dataTableCell}>{barnName}</TableCell>
        <TableCell className={styles.expandTableCell}>
          <Icon className={`fas fa-caret-${isSelected ? 'up' : 'down'}`} />
        </TableCell>
      </TableRow>
      {isSelected && <TableRow><TableCell className={styles.expandedTableCell} colSpan={3}>
        <Table className={styles.expandedTable}><TableBody>
          {columnConfig.showOrderNumberColumn && <TableRow className={styles.tableRow}>
            <TableCell className={styles.headerTableCell}>Order Number</TableCell>
            <TableCell className={styles.dataTableCell}>{order.properties.orderNumber}</TableCell>
          </TableRow>}
          <TableRow className={styles.tableRow}>
            <TableCell className={styles.headerTableCell}>Feed Name</TableCell>
            <TableCell className={styles.dataTableCell}>
              <OrderFeedType orderEntity={order} iconClassName={styles.medicatedIcon} />
            </TableCell>
          </TableRow>
          <TableRow className={styles.tableRow}>
            <TableCell className={styles.headerTableCell}>Bin</TableCell>
            <TableCell className={styles.dataTableCell}>{binNames.join(', ')}</TableCell>
          </TableRow>
          <TableRow className={styles.tableRow}>
            <TableCell className={styles.headerTableCell}>Quantity ({orderMeasurement.unit})</TableCell>
            <TableCell className={styles.dataTableCell}>
              {feedEntity
                ? orderAllocationVolumes.map(volume => `${volume} ${orderMeasurement.unit}`).join(', ')
                : ' - '
              }
            </TableCell>
          </TableRow>
        </TableBody></Table>
      </TableCell></TableRow>
      }
    </>
  );
}
